// ── 계약 저장 뒤 홍보물 주문 만들기·계약 연결 ───────────────────────────────
// utils.js(288줄)에서 떼어냄(2026-10-15, 300줄 규칙 — 4단계 계약 통합 4-1). 함수 본문은 글자 그대로 옮겼다.
import { apiClient } from '../../../api/apiClient';
import { getKstTodayYmd } from './utils';
import { HQ_CONTRACT_API } from './contractApi';

/**
 * 계약 저장 후 홍보물 주문 생성 및 계약 FK 연결
 * validPromoItems 없으면 아무것도 하지 않음
 */
// api: 주소 묶음(contractApi.js, 2026-10-15) — 기본은 본사 주소
export async function savePromoOrder({ form, ticketTypes, allProductCategories, contractId, showToast, api = HQ_CONTRACT_API }) {
  const validPromoItems = (form.promoItems || []).filter(
    (i) => i.item_id && Number(i.quantity) > 0
  );
  const newMemo = String(form.promoMemo || '').trim();
  const hasPromoWork = validPromoItems.length > 0 || Boolean(newMemo);
  if (!hasPromoWork) return true;

  // 새 아이템도 없고 메모도 없으면 아무것도 안 함
  if (!validPromoItems.length && !newMemo) return;

  // 기존 홍보물 주문 ID + 메모 조회 (contractId로 연결된 주문이 있으면 memo 이어붙이기)
  let existingOrderId = null;
  let existingMemo = '';
  if (contractId) {
    try {
      const contractRes = await apiClient.get(`${api.contracts}/by-contract/${contractId}`);
      existingOrderId = contractRes.data?.data?.promotional_order_id
        ?? contractRes.data?.promotional_order_id ?? null;
      if (existingOrderId) {
        const orderRes = await apiClient.get(`${api.promoOrders}/${existingOrderId}`);
        if (orderRes.ok && orderRes.data?.data) {
          existingMemo = orderRes.data.data.memo || '';
        } else {
          existingOrderId = null;
          existingMemo = '';
        }
      }
    } catch { /* 조회 실패 시 기존 메모 없이 진행 */ }
  }

  // 기존 메모 + 새 메모 이어붙이기 (둘 다 있으면 줄바꿈 구분)
  let mergedMemo = [existingMemo, newMemo].filter(Boolean).join('\n') || null;

  // ── 새 아이템 없고 메모만 있는 경우: 기존 주문 메모만 PATCH ─────────────────
  if (!validPromoItems.length) {
    if (existingOrderId && newMemo) {
      const memoRes = await apiClient.patch(`${api.promoOrders}/${existingOrderId}/memo`, { memo: mergedMemo });
      if (!memoRes.ok) {
        showToast('홍보물 메모 저장에 실패했습니다. 계약 저장은 유지되지만 홍보물 관리에서 확인해 주세요.', 'error');
        return false;
      }
    }
    return true;
  }

  if (existingOrderId) {
    const appendRes = await apiClient.post(`${api.promoOrders}/${existingOrderId}/items`, {
      memo: mergedMemo,
      items: validPromoItems.map((i) => ({
        item_id: i.item_id,
        item_name: i.item_name,
        quantity: Number(i.quantity),
        unit_price: i.unit_price,
      })),
    });
    if (!appendRes.ok) {
      if (appendRes.status === 404) {
        existingOrderId = null;
        existingMemo = '';
        mergedMemo = [newMemo].filter(Boolean).join('\n') || null;
      } else {
        showToast('기존 홍보물 주문에 추가하지 못했습니다. 홍보물 관리에서 확인해 주세요.', 'error');
        return false;
      }
    } else {
      return true;
    }
  }

  // 계약 상품(첫 번째)의 category 이름으로 product_categories.id 결정
  const firstProductName = (form.contractProducts || [])[0];
  const firstTicketType  = (ticketTypes || []).find((t) => (t.name || t) === firstProductName);
  const categoryName     = firstTicketType?.category || null;
  const matchedCat       = (allProductCategories || []).find((c) => c.name === categoryName);
  const categoryId       = matchedCat?.id || null;

  const promoRes = await apiClient.post(api.promoOrders, {
    // 회사별·통합 계약 창(2026-10-15)은 이 계약 번호로 주문 회사를 계약 회사로 정한다. 본사 주소는 이 값을 쓰지 않는다.
    contract_id:       contractId || null,
    received_date:     form.promoReceivedDate || getKstTodayYmd(),
    category_id:       categoryId,
    sales_person_name: form.sales_person_name,
    company_name:      form.company_name,
    address:           form.address,
    phone:             form.phone,
    mobile_phone:      form.mobile_phone,
    memo:              mergedMemo,
    items: validPromoItems.map((i) => ({
      item_id: i.item_id, item_name: i.item_name,
      quantity: Number(i.quantity), unit_price: i.unit_price,
    })),
  });

  if (!promoRes.ok) {
    showToast('홍보물 등록 실패 — 홍보물 관리에서 수동으로 입력해 주세요.', 'error');
    return false;
  }
  // 계약에 홍보물 주문 ID 연결 (promotional_order_id FK)
  const promoOrderId = promoRes.data?.id;
  if (contractId && promoOrderId) {
    const linkRes = await apiClient.patch(`${api.contracts}/${contractId}/promo-link`, { promotional_order_id: promoOrderId });
    const linkedPromoId = linkRes.data?.data?.promotional_order_id ?? linkRes.data?.promotional_order_id;
    if (!linkRes.ok || Number(linkedPromoId) !== Number(promoOrderId)) {
      showToast('홍보물은 등록됐지만 계약 연결에 실패했습니다. 홍보물 관리에서 연결 상태를 확인해 주세요.', 'error');
      return false;
    }
  }
  return true;
}
